// CanvasAriaChat.jsx
// ────────────────────────────────────────────────────────────────────────
// Aria's chat panel that sits NEXT TO every project. She's always there.
// When the user opens a project, Aria is right there to help — talking
// specifically about that project, not generic chat.
//
// Per-project conversation thread (persisted in localStorage by projectId).
// Sends `projectId` + the live LayoutSpec snapshot with every message so
// the backend can prepend project context to Aria's reasoning.
//
// Personality stays locked. Only the *context* changes per project.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiBase } from './apiBase';

const _base = getApiBase();
const API_BASE = _base || 'http://127.0.0.1:3001';
const STORAGE_PREFIX = 'aria_canvas_chat__';
const MAX_HISTORY = 60;     // keep last N messages per project
const MAX_CTX_TURNS = 8;    // send last N user/aria turns to the API

function loadHistory(projectId) {
  if (!projectId || typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + projectId);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(-MAX_HISTORY) : [];
  } catch { return []; }
}

function saveHistory(projectId, msgs) {
  if (!projectId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_PREFIX + projectId, JSON.stringify(msgs.slice(-MAX_HISTORY)));
  } catch { /* quota — ignore */ }
}

export default function CanvasAriaChat({ projectId, project, userId, onProjectChanged }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Load history when project changes
  useEffect(() => {
    setMessages(loadHistory(projectId));
  }, [projectId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Persist on change
  useEffect(() => {
    if (projectId && messages.length > 0) saveHistory(projectId, messages);
  }, [projectId, messages]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending || !projectId) return;

    const userMsg = { role: 'user', content: text, ts: Date.now() };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSending(true);

    // Build conversation history payload — Aria's API contract
    const history = [...messages, userMsg]
      .slice(-MAX_CTX_TURNS)
      .map((m) => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content }));

    try {
      const r = await fetch(`${API_BASE}/api/ai/conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId || 'default' },
        body: JSON.stringify({
          initialQuery: text,
          conversationHistory: history,
          userId: userId || 'default',
          communicationMode: 'text',
          skipAudio: true,
          // Force-fresh: every project-scoped chat must bypass canonicals
          // + brain caches so Aria reasons against THIS user's project,
          // not a generic cached answer for the same phrasing.
          forceFresh: true,
          // ── Project context (Amazon-for-AI layer) ──
          // Backend reads these and prepends to Aria's system prompt so her
          // reply is grounded in THIS project, not generic.
          projectContext: project ? {
            id: project.id,
            name: project.name,
            type: project.type,
            description: project.description,
            mode: project.mode,
            panels: (project.layoutSpec?.panels || []).map((p) => ({
              id: p.id,
              type: p.type,
              title: p.title,
              // include data preview for top-scored panels so Aria sees content
              preview: panelPreview(p),
            })),
          } : null,
        }),
      });
      const data = await r.json();
      const reply = (data?.solution || data?.reply || data?.reasoning || data?.question || '').toString().trim();
      const ariaMsg = {
        role: 'assistant',
        content: reply || "I'm here. Tell me more.",
        ts: Date.now(),
        surface: data?.surface || null,
      };
      setMessages((m) => [...m, ariaMsg]);
      // If Aria's response signals a layout change, parent can refresh
      if (data?.projectUpdated && onProjectChanged) onProjectChanged();
    } catch (e) {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: "Network hiccup — try again?",
        ts: Date.now(),
        error: true,
      }]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [input, sending, projectId, project, messages, userId, onProjectChanged]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!projectId) return null;

  return (
    <aside className={`canvas-aria${collapsed ? ' is-collapsed' : ''}`}>
      <header className="canvas-aria__header">
        <div className="canvas-aria__title-row">
          <span className="canvas-aria__orb" aria-hidden />
          <div>
            <div className="canvas-aria__title">Aria</div>
            <div className="canvas-aria__subtitle">
              {project ? `Helping with "${project.name}"` : 'Ready when you are.'}
            </div>
          </div>
        </div>
        <button
          className="canvas-aria__collapse"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? 'Expand' : 'Collapse'}
        >{collapsed ? '◀' : '▶'}</button>
      </header>

      {!collapsed && (
        <>
          <div className="canvas-aria__messages" ref={scrollRef}>
            {messages.length === 0 ? (
              <div className="canvas-aria__empty">
                <p>Ask Aria anything about this project.</p>
                <p className="canvas-aria__empty-hint">
                  She knows what you're working on. Try:
                  <br />“What should I focus on first?”
                  <br />“Add a panel for tracking my budget.”
                  <br />“Help me think through the next step.”
                </p>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`canvas-aria__msg canvas-aria__msg--${m.role}${m.error ? ' is-error' : ''}`}>
                  {m.role === 'assistant' && <div className="canvas-aria__msg-tag">Aria</div>}
                  <div className="canvas-aria__msg-body">{m.content}</div>
                </div>
              ))
            )}
            {sending && (
              <div className="canvas-aria__msg canvas-aria__msg--assistant">
                <div className="canvas-aria__msg-tag">Aria</div>
                <div className="canvas-aria__typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
          </div>

          <div className="canvas-aria__input-wrap">
            <textarea
              ref={inputRef}
              className="canvas-aria__input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={`Talk to Aria about ${project?.name || 'this project'}…`}
              rows={2}
              disabled={sending}
            />
            <button
              className="canvas-aria__send"
              onClick={send}
              disabled={!input.trim() || sending}
              title="Send (Enter)"
            >→</button>
          </div>
        </>
      )}
    </aside>
  );
}

// Build a small data preview for each panel so Aria sees the user's actual content.
function panelPreview(p) {
  const cfg = p.config || {};
  switch (p.type) {
    case 'text':
    case 'notes':
      return (cfg.content || '').slice(0, 240);
    case 'key_values':
      return (cfg.values || []).slice(0, 8).map((v) => `${v.label}: ${v.value}`).join(' | ');
    case 'checklist': {
      const items = cfg.items || [];
      const done = items.filter((i) => i.done).length;
      const sample = items.slice(0, 6).map((i) => `[${i.done ? 'x' : ' '}] ${i.text}`).join(' | ');
      return `${done}/${items.length} done. ${sample}`;
    }
    default:
      return '';
  }
}
